import React, { useEffect, useLayoutEffect } from 'react';

import { css, cx } from '@emotion/css';
import { Card, CardActions, CardContent, Slide } from '@mui/material';

import { FontSizes } from '@/assets/fonts.ts';
import { Icons } from '@/assets/icons.ts';
import { useChatContent } from '@/hooks/useChatContent.ts';
import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useComponentScroll } from '@/hooks/useScroll.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { HandledErrorPage } from '@/pages/HandledErrorPage.tsx';
import { Queries } from '@/queries/Queries';
import { QueryAwait } from '@/queries/QueryAwait.tsx';
import { Theme } from '@/theme/CreateTheme.ts';
import { useScreenSize } from '@/theme/responsive.ts';
import { Button } from '@/uiKit/atoms/Button.tsx';
import { CenteredLoader } from '@/uiKit/atoms/pending/CenteredLoader.tsx';
import { MessagesList } from '@/uiKit/molecules/chat/MessagesList.tsx';
import { SendMessageForm } from '@/uiKit/molecules/chat/SendMessageForm.tsx';
import { useChatMessagingState } from '@/uiKit/organisms/ChatMessaging/ChatMessagingState.ts';

export type ChatMessagesProps = {
  className?: string;
};

export const ChatMessages: React.FC<ChatMessagesProps> = ({ className }) => {
  const chat = useChatMessagingState(state => state);
  const styles = useStyles(makeStyles);
  const patientId = usePatientIdFromURL();
  const me = Queries.user.useMe({});
  const messages = useChatContent(patientId, me.id);
  const hasMessages = messages && messages.content.length > 0;
  const screenSize = useScreenSize();
  const { scrollPosition, onScroll } = useComponentScroll({ reversed: true });

  useLayoutEffect(() => {
    if (
      screenSize &&
      screenSize != 'SM' &&
      screenSize != 'MD' &&
      hasMessages &&
      !chat.touched
    ) {
      chat.openChat();
    }
  }, [screenSize, hasMessages, chat]);

  useEffect(() => {
    if (scrollPosition.isNearBottom) {
      messages.loadMore();
    }
  }, [scrollPosition.isNearBottom]);

  return (
    <Slide
      direction="left"
      in={chat.isOpen}
      timeout={300}
      mountOnEnter
      unmountOnExit
    >
      <Card className={cx(styles.container, className)}>
        <Button
          variant="text"
          className={styles.close}
          onClick={() => chat.toggleChat()}
        >
          <Icons.doubleArrowRight className={styles.closeIcon} />
        </Button>
        <QueryAwait
          errorFallback={HandledErrorPage}
          pendingFallback={CenteredLoader}
        >
          <CardContent className={styles.messages} onScroll={onScroll}>
            <MessagesList patientId={patientId} />
          </CardContent>
          <CardActions className={styles.actions}>
            <SendMessageForm className={styles.sendMessage} />
          </CardActions>
        </QueryAwait>
      </Card>
    </Slide>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: 'flex',
    flexDirection: 'column',
    width: 'min(500px, 95vw)',
  }),
  header: css({
    '.MuiCardHeader-action': {
      alignSelf: 'center',
    },
  }),
  messages: css({
    flex: '1 1',
    display: 'flex',
    overflowY: 'auto',
    flexDirection: 'column-reverse',
    scrollbarColor: 'lightgrey transparent',
    '::-webkit-scrollbar': {
      backgroundColor: 'transparent',
    },
  }),
  icons: css({
    width: FontSizes.body,
    height: FontSizes.body,
    cursor: 'pointer',
  }),
  actions: css({
    padding: 0,
  }),
  sendMessage: css({
    flex: '1 1',
  }),
  close: css({
    position: 'absolute',
    top: 0,
    left: 0,
    padding: theme.spacing(4),
    margin: theme.spacing(4, 0),
    minWidth: 0,
    '.MuiButton-label': {
      padding: 0,
      margin: 0,
    },
  }),
  closeIcon: css({
    width: FontSizes.bodyLarge,
    height: FontSizes.bodyLarge,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
  }),
});
